import React, { useEffect, useMemo, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import PHKLPageContainer from "../../../../components/phkl-page-container/phkl-page-container"; //PHKL Custom
import { Survey } from "survey-react-ui";
import { cloneDeep } from "lodash";
import { FunctionFactory, Model, settings } from "survey-core";
import { useLocation, useNavigate } from "react-router-dom";
import { OtpVerificationTemplate } from "./template";
import { initSurvey } from "../../components/surveyjs/survey/init-survey";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { getFullRegionList } from "../../../common/network/network";
import {
  confirmActionAsyncFunc,
  CustomFunctionName,
} from "../../components/surveyjs/survey/survey.util";
import { getOid, sendOtpFunc } from "../../../prudna/pages/personal-info-form/send-otp";
import { OtpContextEnum, UserTypeEnum } from "../../../common/network/enum";
import { validateOtpFunc } from "./validate-otp";
import { formatRegionDisplay } from "../../../../utils/common-utils";
import {
  TakeUIClickEventParameters,
  TakeUIScreenViewEventParameters,
} from "../../../../common/ga/types/ga-general-type";
import { takeUIClickEvent, takeUIScreenViewEvent } from "../../../../common/ga/ga";
//PHKL Custom -s
import { useQuery } from "../../../../utils/hooks/use-query";
import i18next from "i18next";
import { setItem } from "../../../../utils/local-storage";
//PHKL Custom -e
import { addUniqueErrorToQuestion } from "../form/survey.util";
const elements = OtpVerificationTemplate.pages[0].elements;

let sendOtpRequested = false;
export const OtpVerificationScreen = () => {
  initSurvey();
  // Create a wrapper function
  const wrappedSendOtpFunc = async (params: any[]) => {
    await sendOtpFunc(params);
    sendOtpRequested = true;
    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "create_account",
      stage: "send_otp",
      screen_id: "SCR_LGN_CNDDT_CRT_ACCNT",
      screen_name: "Login-Candidate-Create-Account-Screen",
      object_name: "Send-Code",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);
  };
  // Replace the registered function since we need to add GA tracking in sendOtp
  FunctionFactory.Instance.unregister(CustomFunctionName.sendOtp);
  FunctionFactory.Instance.register(CustomFunctionName.sendOtp, wrappedSendOtpFunc);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  //PHKL Custom -s
  const query: any = useQuery();
  const invitationId = query.get("iid");
  const lang = query.get("lang");
  const userName = location.state?.userName;
  const phoneNum = location.state?.phoneNum;
  //PHKL Custom -e

  const isForgetPassword = location.state?.isForgetPassword;
  const [formValues, setForm] = useState({} as any);
  const [otp, setOtp] = useState(false);
  const [pageInitiating, setPageInitiating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    //PHKL Custom -s
    if (!invitationId && !isForgetPassword) {
      navigate(`${ROOT_PATH}/login`);
      return;
    }
    //PHKL Custom -e

    Promise.all([getFullRegionList()])
      .then((reses: any) => {
        const [regionConfigs] = reses;
        const regionPhoneField = elements.find((item: any) => item.name === "regionPhone");
        if (regionPhoneField?.templateElements?.[0] && regionConfigs) {
          const regionOpts = Object.values(regionConfigs).map((region: any) => ({
            text: formatRegionDisplay(region.code, region.displayName),
            key: region.code,
            value: region.code,
            reg: region.regex,
          }));
          // @ts-ignore
          regionPhoneField.templateElements[0].choices = regionOpts;
          setForm({
            regionCode: regionOpts?.[0].key ?? "+852",
          });
        } else {
          setForm({
            regionCode: "+852",
          });
        }
        //PHKL Custom -s
        if (phoneNum) {
          const arrPhone = phoneNum.split(" ");
          setForm({
            ...formValues,
            regionCode: arrPhone[0],
            phoneNumber: arrPhone[1],
          });
        }
        //PHKL Custom -e

        const otpField: any = elements.find((item: any) => item.name === "otp");
        const otpConfig = {
          ...otpField.otpConfig,
          actionParams: {
            context: isForgetPassword ? OtpContextEnum.FORGOT_PASSWORD : OtpContextEnum.FIRST_LOGIN,
            username: userName, //PHKL Custom
            invitationId: invitationId, //PHKL Custom
            userType: "CANDIDATE",
            deliveryMethod: "SMS",
          },
        };
        otpField.otpConfig = otpConfig;
      })
      .finally(() => {
        setPageInitiating(false);
      });
  }, [isForgetPassword]);

  //PHKL Custom -s
  useEffect(() => {
    if (lang) {
      setItem("i18nextLng", lang.toLocaleLowerCase());
      i18n.changeLanguage(lang.toLocaleLowerCase());
    }
  }, [lang]);

  const template = useMemo(() => {
    const _template = cloneDeep(OtpVerificationTemplate);
    convertTemplate(_template);
    return _template;
  }, [i18next.language, pageInitiating, isForgetPassword]);
  //PHKL Custom -e

  const survey = useMemo(() => {
    if (pageInitiating) return null;

    //PHKL Custom -s
    const elements = template.pages[0].elements;
    if (isForgetPassword) {
      const regionPhoneField = elements.find((element) => element.name === "regionPhone");
      if (regionPhoneField) {
        regionPhoneField.visibleIf = "false";
      }
    }
    //PHKL Custom -e

    const survey = new Model(cloneDeep(template)); //PHKL Custom
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;
    const defaultValue = {
      regionPhone: [
        {
          regionCode: formValues.regionCode ?? "",
          phoneNumber: formValues.phoneNumber,
        },
      ],
    };
    survey.mergeData(defaultValue);
    survey.onComplete.add((sender, options) => {});
    settings.confirmActionAsync = confirmActionAsyncFunc;
    return survey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInitiating, template /*PHKL Custom*/]);

  const handleOnConfirm = async () => {
    const gaData: TakeUIClickEventParameters = {
      module: "Login",
      feature: "CandidateLogin",
      journey: "create_account",
      stage: "send_otp",
      screen_id: "SCR_LGN_CNDDT_CRT_ACCNT",
      screen_name: "Login-Candidate-Create-Account-Screen",
      object_name: "Confirm",
      object_type: "BUTTON",
    };
    takeUIClickEvent(gaData);

    if (!survey) return null;
    if (!survey.validateCurrentPage()) {
      setIsValid(false);
      return;
    }
    setIsLoading(true);

    const oid = getOid();
    if (!oid) {
      setIsValid(false);
      const otpQuestion = survey.getQuestionByName("otp");
      addUniqueErrorToQuestion(otpQuestion, t("Common.incorrect_verification_code"));
    } else {
      const surveyData = survey.data;
      const res = await validateOtpFunc({
        context: isForgetPassword ? OtpContextEnum.FORGOT_PASSWORD : OtpContextEnum.FIRST_LOGIN,
        otp: `${getOid()}:${surveyData.otp}`,
        regionCode: surveyData.regionPhone[0].regionCode,
        phone: surveyData.regionPhone[0].phoneNumber,
        userType: UserTypeEnum.CANDIDATE,
      });
      if (res === true) {
        const otherParams = new URLSearchParams(query.toString()); /*PHKL Custom*/
        otherParams.delete("lang");
        navigate(`${ROOT_PATH}/onboarding/set-password?${otherParams.toString()}`, {
          state: {
            user: {
              username: userName, //PHKL Custom
              phoneNum: `${surveyData.regionPhone[0].regionCode} ${surveyData.regionPhone[0].phoneNumber}`, //PHKL Custom
              otp: `${getOid()}:${surveyData.otp}`,
              context: isForgetPassword
                ? OtpContextEnum.FORGOT_PASSWORD
                : OtpContextEnum.FIRST_LOGIN,
            },
          },
        });
      } else {
        if (typeof res === "string") {
          const otpQuestion = survey.getQuestionByName("otp");
          addUniqueErrorToQuestion(otpQuestion, res);
        }
        setIsValid(false);
      }
    }

    setIsLoading(false);
  };

  const handleValueChanged = (sender: any, options: any) => {
    if (options.name === "otp") {
      setOtp(options.value);
    }
    if (options.name === "regionPhone") {
      setForm({
        regionCode: options.value[0].regionCode,
        phoneNumber: options.value[0].phoneNumber,
      });
    }
  };

  useEffect(() => {
    if (otp && formValues.regionCode && formValues.phoneNumber) {
      setIsValid(true);
      const otpField: any = elements.find((item: any) => item.name === "otp");
      const otpConfig = {
        ...otpField.otpConfig,
        actionParams: {
          context: isForgetPassword ? OtpContextEnum.FORGOT_PASSWORD : OtpContextEnum.FIRST_LOGIN,
          username: userName, //PHKL Custom
          invitationId: invitationId, //PHKL Custom
          userType: "CANDIDATE",
          deliveryMethod: "SMS",
        },
      };
      otpField.otpConfig = otpConfig;
    } else {
      setIsValid(false);
    }
  }, [formValues.phoneNumber, formValues.regionCode, isForgetPassword, otp]);

  return (
    <PHKLPageContainer
      title={isForgetPassword ? t("Common.reset_password") : t("Common.create_account")}
    >
      {" "}
      {/* PHKL Custom */}
      {!pageInitiating && (
        <div className="flex flex-1 flex-col max-w-md px-6">
          <div className="flex flex-col w-full pb-4">
            <div className="bg-white w-full rounded-tr-3xl h-10"></div>
            <Survey
              currentPageNo={0}
              className="survey-common"
              id="create-account"
              model={survey}
              onValueChanged={handleValueChanged}
            />
          </div>
          <div className=" w-full bg-white pb-7 flex items-center justify-between gap-10 pt-16 flex-col">
            <button
              className="bg-red-500 flex-1 text-white font-semibold py-3 rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 disabled:bg-[#CCCCCC] disabled:shadow-none w-full"
              disabled={!isValid || isLoading || !sendOtpRequested}
              onClick={handleOnConfirm}
            >
              {t("app.button.next") /* PHKL Custom */}
            </button>
            {!isForgetPassword && (
              <div className="flex flex-col items-start gap-5 w-full">
                <p
                  className="w-full font-semibold text-sm"
                  style={{
                    color: "#000000",
                  }}
                >
                  {t("already_have_account")}
                </p>
                <button
                  className="w-full flex justify-center items-center py-3 px-4 gap-4 bg-white border-2 border-[#CCCCCC] rounded-xl"
                  onClick={() => navigate(`${ROOT_PATH}/login`)}
                >
                  <span className="font-semibold text-base text-[#333333]">{t("sign_in")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </PHKLPageContainer>
  );
};

const convertTemplate = (template: any /*PHKL Custom*/) => {
  // don't mutate the original template
  // const template: any = cloneDeep(OtpVerificationTemplate); //PHKL Custom

  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };

  // Loop through each element in the first page
  template.pages[0].elements = template.pages[0].elements.map((q: any) => {
    // Translate title, labelTrue, labelFalse, requiredErrorText fields
    translateField(q, "title");
    translateField(q, "placeholder");
    translateField(q, "labelTrue");
    translateField(q, "labelFalse");
    translateField(q, "requiredErrorText");
    translateField(q, "tooltips");
    translateField(q?.modalConfig, "modalTitle");

    // translate choices if they exist
    if (q.choices && Array.isArray(q.choices)) {
      q.choices.forEach((choice: any) => {
        translateField(choice, "text");
      });
    }

    // Check if the element has validators and translate their text fields
    if (q.validators && Array.isArray(q.validators)) {
      q.validators.forEach((validator: any) => {
        translateField(validator, "text");
      });
    }

    // If the element is a paneldynamic type, we need to handle nested templateElements
    if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
      q.templateElements = q.templateElements.map((te: any) => {
        translateField(te, "title");
        translateField(te, "labelTrue");
        translateField(te, "labelFalse");
        translateField(te, "requiredErrorText");
        translateField(te, "placeholder");
        translateField(te?.modalConfig, "modalTitle");

        if (te.validators && Array.isArray(te.validators)) {
          te.validators.forEach((validator: any) => {
            translateField(validator, "text");
          });
        }

        return te;
      });
    }

    return q;
  });
  return template;
};
